/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useCallback, useState } from 'react'

import { useNavigate } from 'react-router-dom'

import { Alert, Button } from '@/components/ui'
import { useApi } from '@/hooks'
import { parseGeotabDatabases } from '@/utils/geotabDatabases'
import logger from '@/utils/logger'

import type { AdminUserDetail } from '../types'

const log = logger({ category: 'AdminUserDetailPage' })

/** Nearly every mapping we set up today points here, so save the typing. */
const DEFAULT_GEOTAB_DATABASE = 'cooltrax_demo'

interface Props {
  user: AdminUserDetail
  uuid: string
}

/** The MyGeotab add-in SSO mapping, with its own save. */
export const GeotabCard: React.FC<Props> = ({ user, uuid }) => {
  const { api } = useApi()
  const navigate = useNavigate()
  const [geotabEnabled, setGeotabEnabled] = useState(Boolean(user.geotabEmail))
  // Default the Geotab email to their Insights email — usually the same person,
  // and the admin only has to change it when the two differ.
  const [geotabEmail, setGeotabEmail] = useState(user.geotabEmail ?? user.email ?? '')
  const [geotabDatabase, setGeotabDatabase] = useState(user.geotabDatabase ?? DEFAULT_GEOTAB_DATABASE)
  // ?? false so a server that predates this field leaves the checkbox controlled.
  const [canSelectDataSource, setCanSelectDataSource] = useState(user.canSelectDataSource ?? false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  // Turning the checkbox on with nothing typed yet should still offer their Insights email.
  const handleToggle = useCallback(
    (checked: boolean) => {
      setGeotabEnabled(checked)
      if (checked && !geotabEmail) setGeotabEmail(user.email ?? '')
      if (checked && !geotabDatabase) setGeotabDatabase(DEFAULT_GEOTAB_DATABASE)
    },
    [geotabEmail, geotabDatabase, user.email]
  )

  const handleSave = async () => {
    setIsSaving(true)
    setSaveError('')
    try {
      const response = await api.put<{ uid: string }, { geotabEmail: string | null; geotabDatabase: string | null }>({
        path: `/app/admin/users/${encodeURIComponent(uuid)}/geotab`,
        origin: 'AdminUserDetailPage#save',
        body: geotabEnabled
          ? { geotabEmail: geotabEmail.trim(), geotabDatabase: geotabDatabase.trim() }
          : { geotabEmail: null, geotabDatabase: null },
      })
      if (response.error) {
        setSaveError(response.message || 'Could not save')
        return
      }

      // Separate endpoint because it is a permission rather than a mapping, and only
      // written when it actually changed so granting/revoking stays visible in the log.
      if (canSelectDataSource !== user.canSelectDataSource) {
        const dataSourceResponse = await api.put<{ uid: string }, { canSelectDataSource: boolean }>({
          path: `/app/admin/users/${encodeURIComponent(uuid)}/data-source`,
          origin: 'AdminUserDetailPage#saveDataSource',
          body: { canSelectDataSource },
        })
        if (dataSourceResponse.error) {
          setSaveError(dataSourceResponse.message || 'Could not save the data source permission')
          return
        }
      }

      navigate('/admin/users')
    } catch (ex) {
      log.error(ex)
      setSaveError('Could not save')
    } finally {
      setIsSaving(false)
    }
  }

  // A value of nothing but commas normalises to empty server-side, so treat it as blank
  // here too rather than letting the save come back a 400.
  const databases = parseGeotabDatabases(geotabDatabase)
  const incomplete = geotabEnabled && (!geotabEmail.trim() || databases.length === 0)

  return (
    <section className="card bg-base-100 shadow">
      <div className="card-body">
        <h2 className="card-title text-base">MyGeotab sign-in</h2>
        <p className="text-sm text-base-content/70">
          Lets this user open Insights from the MyGeotab add-in without a separate login. Both values must match the
          MyGeotab session exactly — the database is what stops another MyGeotab account claiming this user.
        </p>

        {/* Inputs are associated by htmlFor rather than nested inside their label —
            nesting makes the label re-dispatch the click onto the control, which
            double-toggles a checkbox. */}
        <div className="mt-2 flex items-center gap-3">
          <input
            id="geotab-enabled"
            type="checkbox"
            className="checkbox checkbox-primary"
            checked={geotabEnabled}
            onChange={(e) => handleToggle(e.target.checked)}
            data-testid="geotab-enabled"
          />
          <label htmlFor="geotab-enabled" className="label-text cursor-pointer">
            Geotab enabled
          </label>
        </div>

        {geotabEnabled && (
          <div className="mt-2 flex flex-col gap-3">
            <div className="form-control w-full">
              <label htmlFor="geotab-email" className="label-text mb-1 block">
                Geotab email
              </label>
              <input
                id="geotab-email"
                className="input input-bordered w-full"
                value={geotabEmail}
                onChange={(e) => setGeotabEmail(e.target.value)}
                placeholder={user.email}
                data-testid="geotab-email"
              />
              <span className="label-text-alt mt-1 block text-base-content/60">
                Their MyGeotab username. Defaults to their Insights email.
              </span>
            </div>

            <div className="form-control w-full">
              <label htmlFor="geotab-database" className="label-text mb-1 block">
                Geotab database(s)
              </label>
              <input
                id="geotab-database"
                className="input input-bordered w-full"
                value={geotabDatabase}
                onChange={(e) => setGeotabDatabase(e.target.value)}
                placeholder="e.g. LINFOX-AU, or hunt_brothers,cooltrax_demo"
                data-testid="geotab-database"
              />
              {/* Leads with the comma form: the field looks single-valued, so support for
                  several is not discoverable unless it is the first thing stated. */}
              <span className="label-text-alt mt-1 block text-base-content/60">
                <strong>Multiple databases are supported — separate them with commas</strong> (e.g.{' '}
                <code className="rounded bg-base-300 px-1 py-0.5">hunt_brothers,cooltrax_demo</code>). A session in any
                one of them will be accepted. Each name must appear exactly as it does in the MyGeotab URL.
              </span>
              {/* Confirms how the list was read before saving — a stray comma is otherwise
                  invisible, and the entries have to match the MyGeotab URL exactly. */}
              {databases.length > 1 && (
                <span
                  className="label-text-alt mt-1 flex flex-wrap items-center gap-1"
                  data-testid="geotab-database-preview"
                >
                  <span className="text-base-content/60">Will accept:</span>
                  {databases.map((database) => (
                    <span key={database} className="badge badge-ghost badge-sm">
                      {database}
                    </span>
                  ))}
                </span>
              )}
            </div>

            {/* Several Insights profiles can share an email — an Active Directory row and
                a legacy Cooltrax one, or two legacy accounts. Sign-in resolves those by
                Employee No, so surface the exact string to enter rather than making the
                admin work out which username belongs to this row. */}
            {user.userName && (
              <div className="rounded-lg bg-base-200 p-3 text-sm" data-testid="geotab-employee-no-hint">
                If this person has more than one Insights account, set <strong>Employee No</strong> on their MyGeotab
                user to <code className="rounded bg-base-300 px-1 py-0.5">{user.userName}</code> to select this one.
              </div>
            )}
          </div>
        )}

        <div className="divider" />

        <h2 className="card-title text-base">Data source</h2>
        <p className="text-sm text-base-content/70">
          While the Fresh and Timescale back ends run side by side, this lets the user choose which one serves each
          insight and the asset map. Intended for testers comparing the two — everyone else always gets Fresh.
        </p>

        <div className="mt-2 flex items-center gap-3">
          <input
            id="can-select-data-source"
            type="checkbox"
            className="checkbox checkbox-primary"
            checked={canSelectDataSource}
            onChange={(e) => setCanSelectDataSource(e.target.checked)}
            data-testid="can-select-data-source"
          />
          <label htmlFor="can-select-data-source" className="label-text cursor-pointer">
            Can choose the data source
          </label>
        </div>

        {saveError && <Alert message={saveError} type="error" />}

        <div className="card-actions mt-4 justify-end">
          <Button handleClick={() => navigate('/admin/users')} variation="ghost">
            Cancel
          </Button>
          <Button handleClick={() => void handleSave()} isDisabled={isSaving || incomplete} variation="primary">
            {isSaving ? (
              <>
                <span className="loading loading-spinner loading-xs" />
                Saving…
              </>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      </div>
    </section>
  )
}

export default GeotabCard
